// src/components/AbsenceRequestModal.js
import React from "react";

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 20,
  },
  modal: {
    width: "100%",
    maxWidth: 600,
    background: "white",
    borderRadius: 12,
    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
    padding: 20,
    maxHeight: "90vh",
    overflowY: "auto",
  },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
  title: { margin: 0, fontWeight: 700, fontSize: 18, color: "#0f172a" },
  closeBtn: {
    background: "transparent",
    border: "1px solid #e5e7eb",
    color: "#0f172a",
    borderRadius: 8,
    fontSize: 14,
    padding: "6px 10px",
    cursor: "pointer",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "140px 1fr",
    gap: 10,
    padding: "10px 0",
    borderBottom: "1px solid #f1f5f9",
  },
  label: { fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 },
  value: { color: "#0f172a", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },

  // Stack project (bold) over date/time (sub)
  valueStack: { color: "#0f172a", fontSize: 14, display: "flex", flexDirection: "column" },
  valueProject: { fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  // === Nytt: ellipsis på subline också
  valueSub: { fontSize: 13, color: "#64748b", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },

  // Affected shifts list
  listBox: {
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    overflow: "hidden",
    background: "white",
    marginTop: 8,
  },
  listHeader: {
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    fontWeight: 700,
    fontSize: 14,
    color: "#0f172a",
  },
  listItem: {
    display: "grid",
    gridTemplateColumns: "110px 110px 1fr",
    gap: 10,
    padding: "10px 12px",
    borderTop: "1px solid #f8fafc",
    alignItems: "center",
    fontSize: 14,
    color: "#0f172a",
  },
  itemSubtle: { fontSize: 13, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },

  badge: (status) => {
    const base = {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
    };
    if (status === "approved") return { ...base, background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0" };
    if (status === "rejected") return { ...base, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca" };
    return { ...base, background: "#fffbeb", color: "#92400e", border: "1px solid #fde68a" };
  },

  actions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 },
  approve: { background: "#10b981", color: "white", border: "none", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontWeight: 700 },
  reject:  { background: "#ef4444", color: "white", border: "none", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontWeight: 700 },
};

const fmtTime = (t) => (t || "").padStart(5, "0");
const prettyDate = (d) => {
  try {
    if (!d) return "—";
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return dt.toISOString().slice(0, 10);
  } catch {
    return d || "—";
  }
};

// === Nytt: "Inskickat" formatterare i dot-format ===
const two = (n) => String(n).padStart(2, "0");
const formatSubmitted = (iso) => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const ymd = `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
    const hm = `${two(d.getHours())}:${two(d.getMinutes())}`;
    return `${ymd} • ${hm}`;
  } catch {
    return "—";
  }
};

export default function AbsenceRequestModal({
  open,
  request,
  onClose,
  onApprove, // () => void
  onReject,  // () => void
  getProjectNameForShift,
  findAffectedShiftsForRequest,
  ruleName,
  isReadOnly
}) {
  if (!open || !request) return null;

  const shiftRow = () => {
    const project =
      getProjectNameForShift(
        (request.shiftId && request.shiftId._id) || request.shiftId || request.shift
      ) || "—";
    const datePart =
      request.shiftDate && request.shiftStart && request.shiftEnd
        ? `${request.shiftDate} • ${fmtTime(request.shiftStart)}–${fmtTime(request.shiftEnd)}`
        : request.shiftDate
        ? request.shiftDate
        : "—";
    return (
      <div style={styles.row}>
        <div style={styles.label}>Skift</div>
        <div style={styles.valueStack}>
          <span style={styles.valueProject}>{project}</span>
          <span style={styles.valueSub}>{datePart}</span>
        </div>
      </div>
    );
  };

  const affected = request.scope === "period" ? findAffectedShiftsForRequest(request) : [];

  const line = (label, val) => (
    <div style={styles.row}>
      <div style={styles.label}>{label}</div>
      <div style={styles.value}>{val || "—"}</div>
    </div>
  );

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>Frånvaroansökan</h3>
          <button type="button" onClick={onClose} style={styles.closeBtn}>Stäng</button>
        </div>

        {line("Sökande", request.employeeName || request.employeeEmail)}
        {/* === Nytt: Inskickat-rad === */}
        {line("Inskickad", formatSubmitted(request.createdAt))}

        {request.scope === "shift" && shiftRow()}

        {request.scope === "period" &&
          line(
            "Period",
            `${prettyDate(request.dateFrom)}${
              request.dateTo ? ` – ${prettyDate(request.dateTo)}` : ""
            }`
          )}

        {line("Typ", ruleName(request))}

        {request.scope === "shift" &&
          line(
            "Gällande tid",
            request.appliesWholeShift
              ? "Hela passet"
              : `${fmtTime(request.fromTime)} – ${fmtTime(request.toTime)}`
          )}

        <div style={styles.row}>
          <div style={styles.label}>Status</div>
          <div>
            <span style={styles.badge(request.status)}>
              {request.status === "approved"
                ? "Godkänd"
                : request.status === "rejected"
                ? "Nekad"
                : "Väntar"}
            </span>
          </div>
        </div>

        {/* Nya fält: Skäl & Återkoppling */}
        {request.reason ? line("Skäl", request.reason) : null}
        {request.decisionNote ? line("Återkoppling", request.decisionNote) : null}

        {/* Påverkade pass för periodansökningar */}
        {request.scope === "period" && (
          <div style={{ paddingTop: 10 }}>
            <div style={styles.label}>Påverkade pass</div>
            <div style={styles.listBox}>
              <div style={styles.listHeader}>Pass i perioden ({affected.length})</div>
              {affected.length === 0 ? (
                <div style={{ padding: "10px 12px", fontSize: 14, color: "#64748b" }}>
                  Inga pass hittades i schemalistan för detta intervall.
                </div>
              ) : (
                affected.map((s) => (
                  <div key={s._id} style={styles.listItem}>
                    <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.date || "—"}</div>
                    <div style={{ ...styles.itemSubtle }}>
                      {(s.startTime || "—")}–{(s.endTime || "—")}
                    </div>
                    <div style={styles.itemSubtle}>
                      {getProjectNameForShift(s)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {(request.status === "pending" && !isReadOnly) && (
          <div style={styles.actions}>
            <button onClick={onReject} style={styles.reject}>Neka</button>
            <button onClick={onApprove} style={styles.approve}>Godkänn</button>
          </div>
        )}
      </div>
    </div>
  );
}
